/**
 * Custom hook for managing user dialog state.
 *
 * Unifies create and edit dialog states into a single state management hook.
 */

import { useState, useCallback } from 'react';
import type { UserResponse } from '@/lib/api';

/**
 * Dialog mode type.
 */
export type UserDialogMode = 'create' | 'edit' | null;

/**
 * Hook return type for user dialog state.
 */
export interface UseUserDialogReturn {
  /**
   * Current dialog mode (null = closed, "create" = create dialog, "edit" = edit dialog).
   */
  dialogMode: UserDialogMode;
  /**
   * User being edited (null when creating or dialog is closed).
   */
  editingUser: UserResponse | null;
  /**
   * Open the create user dialog.
   */
  openCreateDialog: () => void;
  /**
   * Open the edit user dialog for the given user.
   */
  openEditDialog: (user: UserResponse) => void;
  /**
   * Close the dialog.
   */
  closeDialog: () => void;
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean;
}

/**
 * Custom hook for managing user dialog state.
 */
export function useUserDialog(): UseUserDialogReturn {
  const [dialogMode, setDialogMode] = useState<UserDialogMode>(null);
  const [editingUser, setEditingUser] = useState<UserResponse | null>(null);

  const openCreateDialog = useCallback(() => {
    setDialogMode('create');
    setEditingUser(null);
  }, []);

  const openEditDialog = useCallback((user: UserResponse) => {
    setDialogMode('edit');
    setEditingUser(user);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogMode(null);
    setEditingUser(null);
  }, []);

  const isOpen = dialogMode !== null;

  return {
    dialogMode,
    editingUser,
    openCreateDialog,
    openEditDialog,
    closeDialog,
    isOpen,
  };
}
